import React, { useEffect, useMemo } from 'react';

import { DateFormat, formatDateWithYear } from '@lyearn/core/utils/date/formatDate';
import { DateFormats } from '@lyearn/core/utils/time/types';
import { useTranslation } from '@lyearn/i18n';
import { ChevronDownFill } from '@lyearn/icons';
import { DateVariant, LinkButton, Stack, Typography } from '@lyearn/ui';
import DatePicker from '@/components/DatePicker';

interface Props {
  value?: string;
  onChange: (nextValue: string) => void;
  moduleName?: string;
}

const today = new Date();

const StartsOnDateField: React.FC<Props> = React.memo(({ value, onChange, moduleName }) => {
  const resolvedDate = useMemo(() => (value ? new Date(value) : today), [value]);
  const { t } = useTranslation();

  useEffect(() => {
    const idealDate = resolvedDate < today ? today : resolvedDate;
    if (resolvedDate !== idealDate) onChange(idealDate.toISOString());
  }, [onChange, resolvedDate]);

  return (
    <Stack alignItems="center" className="mt-24 ms-8">
      <Typography color="text-text-secondary" variant="body-short-02">
        {t('1on1s:StartDate', {
          module: moduleName,
        })}
      </Typography>
      <DatePicker
        pickerProps={{
          disablePast: true,
          variant: DateVariant.Date,
          selectedTime: resolvedDate,
          onChange: (selectedTime) => {
            if (!selectedTime) return null;
            onChange(selectedTime.toISOString());
          },
          dateFormat: DateFormats.FullDate,
        }}
        showActionButtons={false}>
        <LinkButton className="items-center ms-4">
          <Typography className="text-interactive-primary" variant="body-short-02">
            {formatDateWithYear(resolvedDate, DateFormat.Date)}
          </Typography>
          <ChevronDownFill className="!h-16 !w-16 ms-2" />
        </LinkButton>
      </DatePicker>
    </Stack>
  );
});

export default StartsOnDateField;
